/* The bridge the desktop shell exposes (apps/desktop/preload.cjs). Absent in a browser. */

// The last three arrived on 2026-09-29. The web app updates with every deploy and the
// installed app does not, so an older app's bridge lacks them: every caller checks.
type Bridge = {
  notify(title: string, body: string): Promise<void>;
  onOpenAssistant(callback: () => void): () => void;
  info(): Promise<{ platform: string; version: string }>;
  onActivity?(callback: (active: boolean) => void): () => void;
  reload?(): Promise<boolean>;
  keepLoaded?(value: boolean): Promise<boolean>;
  // The quick bar, 2026-10-01 (apps/web/src/quick). The main window is told where to go;
  // the rest is for the bar's own window and refused anywhere else.
  onNavigate?(callback: (to: string) => void): () => void;
  quickOpen?(to: string | null): Promise<boolean>;
  quickClose?(): Promise<boolean>;
  quickResize?(height: number): Promise<boolean>;
  onQuickShown?(callback: () => void): () => void;
  clipboardText?(): Promise<string>;
  // A server page saved as a PDF where the person picks, 2026-10-06 (reporting).
  savePdf?(path: string, name: string): Promise<{ saved: boolean; path?: string } | null>;
  // Prompts from the MacBook notch, 2026-10-08 (apps/desktop/notch). False on a Mac
  // without the helper or on Windows: the caller falls back to notify.
  notch?(message: NotchMessage): Promise<boolean>;
  onNotch?(callback: (event: NotchEvent) => void): () => void;
};

export type NotchAction = { id: string; label: string; primary?: boolean };
export type NotchMessage =
  | { type: "show"; id: string; title: string; body: string; icon: "sun" | "lunch" | "back" | "moon"; sticky: boolean; actions: NotchAction[] }
  | { type: "hide"; id: string };
/** What the person did: pressed a button, let it close by itself, or it could not show (no notch, screen locked). */
export type NotchEvent =
  | { type: "action"; id: string; action: string }
  | { type: "dismissed" | "unavailable" | "shown"; id: string };

export const desktop: Bridge | undefined = typeof window === "undefined" ? undefined : (window as unknown as { signetDesktop?: Bridge }).signetDesktop;
export const isDesktop = Boolean(desktop);
export const isMacDesktop = isDesktop && typeof navigator !== "undefined" && /Mac/.test(navigator.platform);

/** A native notification in the desktop app, a quiet no-op in a browser. */
export function notify(title: string, body: string): void {
  desktop?.notify(title, body).catch(() => {});
}

/** Reloads past the cache: through the desktop shell when it can, otherwise as a browser would. */
export function reloadApp(): void {
  if (desktop?.reload) desktop.reload().catch(() => window.location.reload());
  else window.location.reload();
}

/**
 * Whether the server now has a newer build of the web app than the one running.
 *
 * Each build names its script after its content (/assets/index-<hash>.js), so a
 * different name in a freshly fetched index.html is a deploy. Checked every ten
 * minutes while the page is on screen, and when it comes back into view; never
 * in development, where the script is not a built asset.
 */
export async function newerBuildAvailable(): Promise<boolean> {
  const running = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute("src");
  if (!running) return false;
  try {
    const html = await (await fetch("/", { cache: "no-store", credentials: "same-origin" })).text();
    const served = /src="(\/assets\/index-[^"]+\.js)"/.exec(html)?.[1];
    return Boolean(served && served !== running);
  } catch {
    return false;
  }
}
